import React, { useState } from 'react';
import { Badge } from '../ui';

// Mock data for templates
const MOCK_TEMPLATES = [
  {
    id: 1,
    name: 'Assured Shorthold Tenancy',
    code: 'ast',
    country: 'uk',
    countryName: 'United Kingdom',
    countryFlag: '🇬🇧',
    type: 'residential',
    status: 'active',
    version: '2.3.1',
    usageCount: 892,
    lastUpdated: '2026-01-20',
    fields: 24,
    clauses: 18,
    description: 'Standard tenancy agreement for residential properties in England and Wales.',
  },
  {
    id: 2,
    name: 'Scottish Private Residential Tenancy',
    code: 'prt',
    country: 'uk',
    countryName: 'United Kingdom',
    countryFlag: '🇬🇧',
    type: 'residential',
    status: 'active',
    version: '1.5.0',
    usageCount: 234,
    lastUpdated: '2026-01-18',
    fields: 22,
    clauses: 16,
    description: 'Private residential tenancy agreement for Scottish properties.',
  },
  {
    id: 3,
    name: 'Commercial Lease Agreement',
    code: 'commercial_uk',
    country: 'uk',
    countryName: 'United Kingdom',
    countryFlag: '🇬🇧',
    type: 'commercial',
    status: 'active',
    version: '1.2.0',
    usageCount: 119,
    lastUpdated: '2026-01-15',
    fields: 32,
    clauses: 24,
    description: 'Commercial property lease for business premises.',
  },
  {
    id: 4,
    name: 'Mietvertrag (Wohnraum)',
    code: 'mietvertrag',
    country: 'de',
    countryName: 'Germany',
    countryFlag: '🇩🇪',
    type: 'residential',
    status: 'active',
    version: '2.1.0',
    usageCount: 654,
    lastUpdated: '2026-01-19',
    fields: 28,
    clauses: 20,
    description: 'Standard German residential rental agreement compliant with BGB.',
  },
  {
    id: 5,
    name: 'Gewerbemietvertrag',
    code: 'gewerbe',
    country: 'de',
    countryName: 'Germany',
    countryFlag: '🇩🇪',
    type: 'commercial',
    status: 'active',
    version: '1.3.0',
    usageCount: 238,
    lastUpdated: '2026-01-17',
    fields: 30,
    clauses: 22,
    description: 'German commercial property lease agreement.',
  },
  {
    id: 6,
    name: 'Bail d\'habitation',
    code: 'bail_habitation',
    country: 'fr',
    countryName: 'France',
    countryFlag: '🇫🇷',
    type: 'residential',
    status: 'active',
    version: '2.0.0',
    usageCount: 456,
    lastUpdated: '2026-01-16',
    fields: 26,
    clauses: 19,
    description: 'French residential lease agreement compliant with Loi ALUR.',
  },
  {
    id: 7,
    name: 'Bail Commercial',
    code: 'bail_commercial',
    country: 'fr',
    countryName: 'France',
    countryFlag: '🇫🇷',
    type: 'commercial',
    status: 'active',
    version: '1.4.0',
    usageCount: 198,
    lastUpdated: '2026-01-14',
    fields: 34,
    clauses: 26,
    description: 'French commercial lease agreement (3-6-9 format).',
  },
  {
    id: 8,
    name: 'Contrato de Arrendamiento',
    code: 'arrendamiento',
    country: 'es',
    countryName: 'Spain',
    countryFlag: '🇪🇸',
    type: 'residential',
    status: 'active',
    version: '1.8.0',
    usageCount: 345,
    lastUpdated: '2026-01-12',
    fields: 25,
    clauses: 17,
    description: 'Spanish residential rental contract compliant with LAU.',
  },
  {
    id: 9,
    name: 'Contratto di Locazione',
    code: 'locazione',
    country: 'it',
    countryName: 'Italy',
    countryFlag: '🇮🇹',
    type: 'residential',
    status: 'active',
    version: '1.6.0',
    usageCount: 289,
    lastUpdated: '2026-01-10',
    fields: 27,
    clauses: 18,
    description: 'Italian residential lease agreement.',
  },
  {
    id: 10,
    name: 'Australian Residential Tenancy',
    code: 'aus_residential',
    country: 'au',
    countryName: 'Australia',
    countryFlag: '🇦🇺',
    type: 'residential',
    status: 'draft',
    version: '0.9.0',
    usageCount: 0,
    lastUpdated: '2026-01-22',
    fields: 23,
    clauses: 15,
    description: 'Australian residential tenancy agreement (in development).',
  },
];

// Type badge component
function TypeBadge({ type }) {
  const config = {
    residential: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Residential' },
    commercial: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Commercial' },
    mixed: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Mixed Use' },
  };

  const style = config[type] || config.residential;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

// Status badge component
function StatusBadge({ status }) {
  const config = {
    active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active' },
    draft: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Draft' },
    archived: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Archived' },
  };

  const style = config[status] || config.draft;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

// Template Card Component
function TemplateCard({ template, onEdit, onDuplicate, onPreview }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
        <div className="flex items-center justify-between">
          <span className="text-3xl">{template.countryFlag}</span>
          <div className="flex gap-2">
            <TypeBadge type={template.type} />
            <StatusBadge status={template.status} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
        <p className="text-sm text-gray-500 mb-3">{template.countryName}</p>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2 bg-gray-50 rounded-xl">
            <p className="text-lg font-bold text-gray-900">{template.fields}</p>
            <p className="text-xs text-gray-500">Fields</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-xl">
            <p className="text-lg font-bold text-gray-900">{template.clauses}</p>
            <p className="text-xs text-gray-500">Clauses</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-xl">
            <p className="text-lg font-bold text-gray-900">{template.usageCount}</p>
            <p className="text-xs text-gray-500">Uses</p>
          </div>
        </div>

        {/* Version & Date */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            v{template.version}
          </span>
          <span>Updated {new Date(template.lastUpdated).toLocaleDateString()}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(template)}
            className="flex-1 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => onPreview(template)}
            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => onDuplicate(template)}
            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Template Editor Modal
function TemplateEditorModal({ template, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: template?.name || '',
    code: template?.code || '',
    country: template?.country || '',
    type: template?.type || 'residential',
    status: template?.status || 'draft',
    description: template?.description || '',
    version: template?.version || '1.0.0',
  });

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'fields', label: 'Fields' },
    { id: 'clauses', label: 'Clauses' },
    { id: 'preview', label: 'Preview' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {template ? 'Edit Template' : 'Create New Template'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {template ? `Editing ${template.name}` : 'Create a new contract template'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Assured Shorthold Tenancy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., ast"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select country</option>
                    <option value="uk">United Kingdom</option>
                    <option value="de">Germany</option>
                    <option value="fr">France</option>
                    <option value="es">Spain</option>
                    <option value="it">Italy</option>
                    <option value="au">Australia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="mixed">Mixed Use</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Brief description of the template..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  className="w-full max-w-[200px] px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="1.0.0"
                />
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Template Fields</h3>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Field
                </button>
              </div>

              {/* Sample fields list */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                {['landlord_name', 'tenant_name', 'property_address', 'monthly_rent', 'deposit_amount', 'lease_start_date'].map((field, idx) => (
                  <div key={field} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-indigo-100 rounded flex items-center justify-center text-xs font-medium text-indigo-600">
                        {idx + 1}
                      </span>
                      <span className="font-mono text-sm text-gray-700">{field}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                        {idx < 3 ? 'text' : idx === 3 || idx === 4 ? 'number' : 'date'}
                      </span>
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button className="p-1 hover:bg-rose-100 rounded transition-colors">
                        <svg className="w-4 h-4 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'clauses' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Contract Clauses</h3>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Clause
                </button>
              </div>

              {/* Sample clauses */}
              <div className="space-y-3">
                {[
                  { title: 'Parties to the Agreement', required: true },
                  { title: 'Property Description', required: true },
                  { title: 'Term of Tenancy', required: true },
                  { title: 'Rent and Payment Terms', required: true },
                  { title: 'Security Deposit', required: true },
                  { title: 'Maintenance and Repairs', required: false },
                  { title: 'Termination Conditions', required: false },
                ].map((clause, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-sm font-medium text-purple-600">
                          {idx + 1}
                        </span>
                        <div>
                          <h4 className="font-medium text-gray-900">{clause.title}</h4>
                          <p className="text-xs text-gray-500">
                            {clause.required ? 'Required clause' : 'Optional clause'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {clause.required && (
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-600 rounded text-xs font-medium">
                            Required
                          </span>
                        )}
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-200 text-center">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="font-medium text-gray-900 mb-1">Template Preview</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Preview how the generated contract will look with sample data
                </p>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Generate Preview
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-white transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-white transition-colors">
              Save as Draft
            </button>
            <button
              onClick={() => onSave(formData)}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              Save Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminTemplates() {
  const [templates, setTemplates] = useState(MOCK_TEMPLATES);
  const [viewMode, setViewMode] = useState('grid');
  const [filter, setFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Get unique countries from templates
  const countries = [...new Set(templates.map(t => t.country))].map(code => {
    const template = templates.find(t => t.country === code);
    return { code, name: template.countryName, flag: template.countryFlag };
  });

  // Filter templates
  const filteredTemplates = templates.filter((template) => {
    const matchesType = filter === 'all' || template.type === filter;
    const matchesCountry = countryFilter === 'all' || template.country === countryFilter;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesCountry && matchesSearch;
  });

  // Stats
  const stats = {
    total: templates.length,
    active: templates.filter(t => t.status === 'active').length,
    residential: templates.filter(t => t.type === 'residential').length,
    commercial: templates.filter(t => t.type === 'commercial').length,
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleDuplicate = (template) => {
    const newTemplate = {
      ...template,
      id: templates.length + 1,
      name: `${template.name} (Copy)`,
      code: `${template.code}_copy`,
      status: 'draft',
      usageCount: 0,
    };
    setTemplates([...templates, newTemplate]);
  };

  const handlePreview = (template) => {
    // Would open preview modal
    console.log('Preview template:', template);
  };

  const handleSave = (formData) => {
    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === editingTemplate.id ? { ...t, ...formData } : t));
    } else {
      setTemplates([...templates, { id: templates.length + 1, ...formData, usageCount: 0, fields: 0, clauses: 0 }]);
    }
    setShowModal(false);
    setEditingTemplate(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contract Templates</h1>
          <p className="text-gray-600 mt-1">Manage and configure contract templates for all countries</p>
        </div>
        <button
          onClick={() => { setEditingTemplate(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Template
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Templates</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.residential}</p>
              <p className="text-sm text-gray-500">Residential</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.commercial}</p>
              <p className="text-sm text-gray-500">Commercial</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Type Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>

          {/* Country Filter */}
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Countries</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Templates Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onPreview={handlePreview}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Template</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Country</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Type</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Status</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Usage</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{template.name}</p>
                      <p className="text-sm text-gray-500 font-mono">{template.code}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="flex items-center gap-2">
                      <span className="text-xl">{template.countryFlag}</span>
                      <span className="text-gray-700">{template.countryName}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <TypeBadge type={template.type} />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={template.status} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{template.usageCount.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No templates found</h3>
          <p className="text-gray-500">Try adjusting your search or filter</p>
        </div>
      )}

      {/* Editor Modal */}
      {showModal && (
        <TemplateEditorModal
          template={editingTemplate}
          onClose={() => { setShowModal(false); setEditingTemplate(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
